import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from "@heroui/react";

import {
  CheckoutRoom,
  CheckoutSwitchState,
  RoomMwCheckout,
  canCancelRow,
  canCheckoutRow,
  cancelAllowed,
  checkoutDisabledCode,
  checkoutDisabledText,
  checkoutOffBannerText,
  checkoutOffForHotel,
  checkoutSwitchedOffHotelText,
  checkoutFastRefreshMs,
  checkoutStatusText,
  confirmCheckoutText,
  fetchCheckout,
  isActiveCheckoutState,
  lastCheckoutText,
  mwCheckoutToRow,
  postCheckout,
  rateLimitedFallbackSeconds,
  tvHasCheckout,
  tvUnavailableText,
} from '@/features/checkout/checkoutApi';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import { ButtonsModal } from '@/components/Common/Modals';

// ---------------------------------------------------------------------------
// Admin Checkout panel on the room page (ticket #91, §2.5).
//
// Two things this gets right on purpose:
//
//  - It opens with NO extra call. Admin room reads already carry the room's
//    checkout as `mw_checkout` (§3.8), so the first paint comes from the
//    project read the page already did. The GET below only runs while a
//    request is actually moving.
//
//  - It addresses the room by its STORED number. A rename typed into the room
//    number box but not yet saved must never send a checkout to a different
//    room, so the caller passes storedRoomNo and this panel never looks at
//    room.room_no.
// ---------------------------------------------------------------------------

// The Info tab's own label/value row and section header. Copied rather than
// imported from ProjectRoom/Project so this panel stays a leaf: ProjectRoom
// imports it, and reaching back for its helpers would close a module cycle.
const PanelHeader = (props: { label: string }) => (
  <FlexRow className="mt-[16px] pb-[8px] b14 text-black">
    <span>
      { props.label }
    </span>
  </FlexRow>
);

const PanelField = (props: { label: string; children?: React.ReactNode }) => (
  <FlexRow className="min-h-[24px] py-[15px] border-solid border-t-1 border-[--Mid-Grey]">
    <FlexRow className="font-medium r14 !text-black min-w-[200px]">
      { props.label }
    </FlexRow>
    <FlexRow className="justify-end">
      { props.children }
    </FlexRow>
  </FlexRow>
);

export interface RoomCheckoutPanelProps {
  projectId:			string;
  roomNo:				string;		// the STORED number, never the typed one
  roomName:				string;
  // As the project read STORED it. `undefined`/`null` means "nothing to show
  // yet" (§2.5), which is a different thing from a room with no checkout.
  mwCheckout?:			RoomMwCheckout | null;
  // Admins see it; empty means no TV has ever activated this room.
  duid?:				string;
  // The project's own on/off switch. A switched-off hotel cannot reach its TVs.
  projectEnabled:		boolean;
  // The System settings Checkout switch as /master.json last said (§3.2).
  // Only the first word: the panel follows `checkout_enabled` from its own
  // /checkout.json answers once it has one. Omitted reads as on.
  checkoutEnabled?:		boolean;
  // #91 §0.8 — whether the Checkout is on for THIS hotel (its own value, else
  // System settings), from the project read and /master.json. Also only the
  // first word, like checkoutEnabled. Omitted reads as checkoutEnabled.
  projectCheckoutEnabled?:	boolean;
}

const RoomCheckoutPanel = (props: RoomCheckoutPanelProps) => {
  const { t } = useTranslation();

  // The row as it stands. Seeded from the project read, then replaced by every
  // GET and by every POST answer.
  const [row, setRow] = React.useState(null as CheckoutRoom | null);
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState("");
  // 404 room_not_found: the panel has nothing to be about any more.
  const [unavailable, setUnavailable] = React.useState(false);
  // `checkout_enabled` + `project_checkout_enabled` from the last
  // /checkout.json answer. null = not asked yet: the props stand.
  const [serverSwitch, setServerSwitch] = React.useState(null as CheckoutSwitchState | null);
  // Learned from the first answer that carries it. null = not asked yet, which
  // is treated as "allowed" — the POST refuses with the right sentence if the
  // server-wide brake is on (§3.7 checkout_paused).
  const [serverAllows, setServerAllows] = React.useState(null as boolean | null);
  const [holdMs, setHoldMs] = React.useState(0);
  const [hidden, setHidden] = React.useState(
    typeof document !== "undefined" ? document.hidden : false
  );
  const [tick, setTick] = React.useState(0);
  const [confirming, setConfirming] = React.useState(false);

  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Re-seed whenever the page moves to another room, or the project read comes
  // back with a newer `mw_checkout`.
  React.useEffect(() => {
    setRow(mwCheckoutToRow(props.roomNo, props.roomName, props.mwCheckout || null));
    setNotice("");
    setUnavailable(false);
    setHoldMs(0);
  }, [props.projectId, props.roomNo, props.roomName, props.mwCheckout]);

  // A fresh /master.json (e.g. after the switch was saved) is newer than the
  // last /checkout.json answer, so it takes over again.
  React.useEffect(() => {
    setServerSwitch(null);
  }, [props.checkoutEnabled, props.projectCheckoutEnabled]);

  React.useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);

    document.addEventListener("visibilitychange", onVisibility);

    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const load = React.useCallback(async () => {
    if (	!props.projectId ||
			!props.roomNo
		 )
      return;

    const res = await fetchCheckout(props.projectId, props.roomNo);

    if (!mounted.current)
      return;

    if (res.kind === "ok") {
      setServerAllows(res.table.can_checkout);
      setServerSwitch({
        checkout_enabled:			res.table.checkout_enabled,
        project_checkout_enabled:	res.table.project_checkout_enabled,
      });
      setNotice("");
      setHoldMs(0);

      // A single-room read answers with exactly one row.
      if (res.table.rooms.length)
        setRow(res.table.rooms[0]);

      setTick((n) => n + 1);
      return;
    }

    const err = res.error;

    if (err.status === 401)
      return;

    // §2.5 — a 404 hides the panel. Either the room is gone (room_not_found)
    // or the project is (project_not_found); either way there is nothing to
    // show here.
    if (err.status === 404) {
      setUnavailable(true);
      return;
    }

    // §2.5 — a 503 KEEPS the last row and retries. Same for a 429.
    const seconds =	err.status === 429
					?	(err.retryAfter || rateLimitedFallbackSeconds)
					:	(err.retryAfter || 0);

    setNotice(err.message);
    setHoldMs(seconds > 0 ? seconds * 1000 : 0);
    setTick((n) => n + 1);
  }, [props.projectId, props.roomNo]);

  const active = isActiveCheckoutState(row && row.checkout ? row.checkout.state : undefined);

  // §6.7 — 5 s while the row is pending / waiting_no_tv, and NO
  // idle refresh at all otherwise: nothing on this panel changes unless a
  // checkout is in flight. The effect's cleanup is what stops it when the page
  // is left.
  React.useEffect(() => {
    if (	unavailable ||
			hidden ||
			(!active && holdMs <= 0)
		 )
      return;

    const delay = holdMs > 0 ? holdMs : checkoutFastRefreshMs;

    const timer = setTimeout(() => { void load(); }, delay);

    return () => clearTimeout(timer);
  }, [unavailable, hidden, active, holdMs, tick, load]);

  // §3.2 / #91 §0.8 — the switches: the panel's own last answer wins over
  // what the project read and /master.json said.
  const switchState: CheckoutSwitchState = React.useMemo(() => {
    if (serverSwitch)
      return serverSwitch;

    const system = props.checkoutEnabled !== false;

    return {
      checkout_enabled:			system,
      project_checkout_enabled:	props.projectCheckoutEnabled !== undefined
      							?	props.projectCheckoutEnabled
      							:	system,
    };
  }, [serverSwitch, props.checkoutEnabled, props.projectCheckoutEnabled]);

  const send = React.useCallback(async (action: "checkout" | "cancel") => {
    if (	!props.projectId ||
			!props.roomNo ||
			busy
		 )
      return;

    setBusy(true);

    const res = await postCheckout({
      projectId:	props.projectId,
      roomNo:		props.roomNo,
      action:		action,
      checkoutId:	row && row.checkout ? row.checkout.id : "",
    });

    if (!mounted.current)
      return;

    setBusy(false);

    if (res.kind === "ok") {
      // §2.5 — the POST answer carries the new row; no GET needed.
      setRow(res.room);
      setNotice("");
      setHoldMs(0);
      setTick((n) => n + 1);
      return;
    }

    const err = res.error;

    if (err.status === 401)
      return;

    if (err.status === 404) {
      setUnavailable(true);
      return;
    }

    // Every 409 but project_disabled carries the room's current row (§3.7).
    if (err.row)
      setRow(err.row);

    // §3.2 / #91 §0.8 — a press that raced a Checkout switch. When the latest
    // word already says off, its banner carries the sentence. When it still
    // says on, the switch changed meanwhile: the generic sentence, and the
    // room is read again at once so the right banner comes up.
    if (err.code === checkoutDisabledCode) {
      setServerAllows(false);
      if (!checkoutOffForHotel(switchState)) {
        setNotice(checkoutDisabledText(switchState));
        void load();
      }
      setTick((n) => n + 1);
      return;
    }

    setNotice(err.message);

    if (	err.status === 429 ||
			err.status === 503
		 ) {
      const seconds = err.retryAfter || (err.status === 429 ? rateLimitedFallbackSeconds : 0);
      setHoldMs(seconds > 0 ? seconds * 1000 : 0);
    }

    setTick((n) => n + 1);
  }, [props.projectId, props.roomNo, row, busy, switchState, load]);

  if (unavailable) {
    return (
    <React.Fragment>
      <PanelHeader label={t('projects:project:rooms:info:checkout')} />
      <div className="r12 text-[--Dark-Grey] py-[8px]">
        { t('mw_light:panel:unavailable') }
      </div>
    </React.Fragment>
    );
  }

  // #91 §0.8 — "off" comes from the hotel's state, never from the System
  // settings switch alone.
  const checkoutEnabled = !checkoutOffForHotel(switchState);
  const checkoutOffBanner = checkoutOffBannerText(switchState);

  const canAct =	props.projectEnabled &&
					checkoutEnabled &&
					serverAllows !== false;

  // Cancel stays usable for a waiting request while the Checkout is off.
  const canCancel = cancelAllowed(serverAllows !== false, checkoutEnabled, props.projectEnabled);

  // Why the button is off, but ONLY for the causes the panel does not already
  // show. "A checkout is already running" is deliberately "" — the Status
  // field says that, in better words, one line up. Checkout switched off has
  // its banner instead.
  const blockedReason =	!props.projectEnabled
						?	t('mw_light:panel:project_disabled')
						:	!checkoutEnabled
						?	""
						:	serverAllows === false
						?	t('mw_light:checkout_unavailable')
						:	"";

  // §2.5 — with `mw_checkout` absent there is nothing to show yet: the TV is
  // either missing or has an app that predates checkout, and there is no
  // history either. The room's own DUID says which of the two it is, and
  // neither case gets a button (§5.1).
  const emptyText =	(props.duid || "").trim() === ""
					?	tvUnavailableText("none")
					:	tvUnavailableText("update_needed");

  return (
  <React.Fragment>
    <PanelHeader label={t('projects:project:rooms:info:checkout')} />
  {/* §3.2 / §0.8 — the Checkout is off for this hotel. */}
  {!checkoutEnabled &&
    <div className="r14 max-w-[448px] rounded-[4px] bg-[#FFF4E5] text-[#8A5300] px-[12px] py-[8px] mb-[8px]">
      {	checkoutOffBanner === checkoutSwitchedOffHotelText
      	?	t('mw_light:checkout_off_hotel_banner')
      	:	t('mw_light:checkout_off_banner')
      }
    </div>
  }
  {!row &&
    <PanelField label={t('mw_light:panel:status')}>
      <span className="r14 text-[--Dark-Grey]">
        { emptyText }
      </span>
    </PanelField>
  }
  {row &&
    <React.Fragment>
      <PanelField label={t('mw_light:panel:last_checkout')}>
        <span className="r14 !text-black">
          { lastCheckoutText(row.last_checkout) }
        </span>
      </PanelField>
      <PanelField label={t('mw_light:panel:status')}>
        <span className="r14 !text-black text-right">
          {	checkoutStatusText(row.checkout)
          	?	checkoutStatusText(row.checkout)
          	:	tvUnavailableText(row.tv) || "-"
          }
        </span>
      </PanelField>
      <FlexRow className="gap-[12px] py-[8px]"
               justify="start">
      {/* Cancel does NOT depend on `tv` (§5.1): a request that is already
          waiting can always be called back, even if the TV's capabilities
          changed since it was made. */}
      {canCancelRow(row, canCancel)
        ?	<Button caption={t('mw_light:cancel')}
        	        layout="white"
        	        isDisabled={busy}
        	        onClick={() => void send("cancel")} />
        :	(	tvHasCheckout(row.tv)
        		?	// A ready TV keeps its button even with the hotel switched
        		    // off — disabled, with the reason beside it.
        		    <React.Fragment>
        		      <Button caption={t('mw_light:checkout')}
        		              icon="cleaning_services"
        		              isDisabled={busy || !canCheckoutRow(row, canAct)}
        		              onClick={() => setConfirming(true)} />
        		    {/* Only a reason that is NOT already on the panel. A button
        		        greyed out because a checkout is in flight needs no text:
        		        the Status line right above it says "Waiting for TV …",
        		        and repeating it as "Checkout is not available right now"
        		        reads like a fault when nothing is wrong. */}
        		    {blockedReason !== "" &&
        		      <span className="r12 text-[--Dark-Grey]">
        		        { blockedReason }
        		      </span>
        		    }
        		    </React.Fragment>
        		:	// §5.1 — no button at all for a TV that never reported its
        		    // capabilities; the text takes its place.
        		    <span className="r12 text-[--Dark-Grey]">
        		      { tvUnavailableText(row.tv) }
        		    </span>
        	)
      }
      {busy &&
        <CircularProgress aria-label="Sending checkout" size="sm" />
      }
      </FlexRow>
    </React.Fragment>
  }
  {notice !== "" &&
    <div className="r12 text-[--Warning] max-w-[448px] pb-[8px]">
      { notice }
    </div>
  }
  {confirming && row &&
    <ButtonsModal title={t('mw_light:confirm:title')}
                  message={confirmCheckoutText(row.room_no, row.name)}
                  buttons={[
                    { value: "no",	caption: t('mw_light:confirm:keep'),	layout: "white" },
                    { value: "yes",	caption: t('mw_light:confirm:yes'),		layout: "warning_bg" },
                  ]}
                  onResult={(_obj: object, answer: string) => {
                    setConfirming(false);

                    if (answer === "yes")
                      void send("checkout");
                  }}>
      <>
      </>
    </ButtonsModal>
  }
  </React.Fragment>
  );
};

export default RoomCheckoutPanel;
